import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Center, Column, DashedSeparator, Row, Text } from '~/commonComponents';
import { IReferrer, ReferralStatus } from '~/domain/Referral';
import { IReferral, ReferralListStatuses, UserTypeEnum } from '~/models';
import { EmptyReferral } from './EmptyReferral';

import BriefcaseIcon from '~/images/briefcase.svg';
import CalendarCheckIcon from '~/images/calendar-check-icon.svg';
import DollarIcon from '~/images/dollar-icon.svg';
import HourglassIcon from '~/images/hourglass.svg';
import HouseIcon from '~/images/house-icon.svg';
import MemoCircleCheckIcon from '~/images/memo-circle-check.svg';
import UserIcon from '~/images/user-icon.svg';

interface IReferralListProps {
  data: IReferral[];
  style?: React.CSSProperties;
  referrer: IReferrer | null;
}

export const ReferralList = ({ data = [], referrer, style }: IReferralListProps) => {
  const showReferredMeSection = !!referrer;

  const referralStatusIconMap = {
    [ReferralListStatuses.AccountCreated]: MemoCircleCheckIcon,
    [ReferralListStatuses.FirstShiftScheduled]: CalendarCheckIcon,
    [ReferralListStatuses.FirstShiftCompleted]: BriefcaseIcon,
    [ReferralListStatuses.PaymentSent]: DollarIcon,
  };

  const referralStatusOrder = [
    ReferralListStatuses.AccountCreated,
    ReferralListStatuses.FirstShiftScheduled,
    ReferralListStatuses.FirstShiftCompleted,
    ReferralListStatuses.PaymentSent,
  ];

  const renderStatusIcon = (status: ReferralListStatuses) => {
    const Icon = referralStatusIconMap[status];

    return Icon ? (
      <img src={Icon} alt="icon" style={{ width: 19, height: 19, marginLeft: 8 }} />
    ) : null;
  };

  const getReferralStatusText = (status: ReferralListStatuses) => {
    return status.replace('First', '1st');
  };

  const orderedData = data.sort((a, b) => {
    return (
      referralStatusOrder.indexOf(a.referralStatus) - referralStatusOrder.indexOf(b.referralStatus)
    );
  });

  return (
    <Box width={'549px'} style={style}>
      {(showReferredMeSection || data?.length > 0) && (
        <Typography
          italic
          color={colors.neutral[500]}
          style={{ fontSize: 16, textAlign: 'center', marginTop: 24, marginBottom: 8 }}
        >
          Track their progress step by step here: Account Created, Shift Scheduled,
          <br />
          Shift Completed, Payment Sent.
        </Typography>
      )}

      {showReferredMeSection && (
        <Column marginTop={16}>
          <Row alignItems="center" gap={12} paddingBottom={20}>
            <DashedSeparator width={180} />
            <Text bold fontSize={18} color="primary_500">
              Who Referred Me
            </Text>
            <DashedSeparator width={180} />
          </Row>

          <Row alignItems="center" justifyContent="space-between">
            <Row alignItems="center">
              <img src={UserIcon} alt="icon" style={{ width: 24, height: 24, marginRight: 5 }} />
              <Text
                bold
                maxWidth={320}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {referrer.name}
              </Text>
            </Row>

            <Row alignItems="center">
              <Text fontSize={16} color="neutral_500">
                {referrer.status === ReferralStatus.Paid ? 'Payment Sent' : 'In Progress'}
              </Text>
              <img
                src={referrer.status === ReferralStatus.Paid ? DollarIcon : HourglassIcon}
                alt="icon"
                style={{ width: 19, height: 19, marginLeft: 5 }}
              />
            </Row>
          </Row>
        </Column>
      )}

      {showReferredMeSection && (
        <Center width="100%" marginTop={32} marginBottom={-16}>
          <Row alignItems="center" gap={12}>
            <DashedSeparator width={205} />
            <Text bold fontSize={18} color="primary_500">
              My Referrals
            </Text>
            <DashedSeparator width={205} />
          </Row>
        </Center>
      )}

      {data?.length === 0 && <EmptyReferral />}

      {data?.length > 0 && (
        <>
          {orderedData.map((item, idx) => (
            <Row
              key={item.id}
              paddingTop={24}
              paddingBottom={22}
              alignItems="center"
              justifyContent="space-between"
              style={{
                borderBottom: idx === data.length - 1 ? 'none' : '1px solid #EDEDED',
              }}
            >
              <Row alignItems="center">
                <img
                  src={item.referredUserType === UserTypeEnum.DNT ? HouseIcon : UserIcon}
                  alt={item.referredUserType === UserTypeEnum.DNT ? 'house-icon' : 'user-icon'}
                  style={{ width: 24, height: 24, marginRight: 5 }}
                />
                <Text bold>{item.referredName}</Text>
              </Row>

              <Row alignItems="center">
                <Text color="neutral_500">{getReferralStatusText(item.referralStatus)}</Text>
                {renderStatusIcon(item.referralStatus)}
              </Row>
            </Row>
          ))}
        </>
      )}
    </Box>
  );
};
